import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  PageHeader,
  Button,
  Card,
  EmptyState,
  Input,
  Select,
  Badge,
  Spinner,
  Modal,
  ModalFooter,
  useToast,
} from '@orientamos/ui'
import {
  Plus,
  BookOpen,
  Search,
  Trash2,
  Edit,
  ChevronDown,
  ChevronRight,
  GripVertical,
  ArrowUp,
  ArrowDown,
} from 'lucide-react'
import {
  listClauses,
  deleteClause,
  reorderClauses,
  type ClauseListItem,
  type ClauseCategory,
  type ClauseFilters,
} from '@lib/api'
import { ClauseModal } from '@features/clauses/components/ClauseModal'

// Category labels in Spanish
const CATEGORY_LABELS: Record<ClauseCategory, string> = {
  services: 'Servicios',
  grace_period: 'Periodo de Gracia',
  rent_increase: 'Incrementos',
  renewal: 'Renovacion',
  pets: 'Mascotas',
  modifications: 'Modificaciones',
  insurance: 'Seguros',
  utilities: 'Servicios Publicos',
  other: 'Otros',
}

// Category order for display
const CATEGORY_ORDER: ClauseCategory[] = [
  'services',
  'grace_period',
  'rent_increase',
  'renewal',
  'pets',
  'modifications',
  'insurance',
  'utilities',
  'other',
]

const categoryOptions = [
  { value: '', label: 'Todas las categorias' },
  ...CATEGORY_ORDER.map((cat) => ({ value: cat, label: CATEGORY_LABELS[cat] })),
]

const contractTypeOptions = [
  { value: '', label: 'Todos los tipos' },
  { value: 'residential', label: 'Residencial' },
  { value: 'commercial', label: 'Comercial' },
]

const statusOptions = [
  { value: '', label: 'Todos los estados' },
  { value: 'true', label: 'Activas' },
  { value: 'false', label: 'Inactivas' },
]

export default function ClausesPage() {
  const { addToast } = useToast()
  const [clauses, setClauses] = useState<ClauseListItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [search, setSearch] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [contractTypeFilter, setContractTypeFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  // Expanded categories (accordion)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(CATEGORY_ORDER))

  // Modal state
  const [modalOpen, setModalOpen] = useState(false)
  const [editingClause, setEditingClause] = useState<ClauseListItem | null>(null)

  // Delete modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [clauseToDelete, setClauseToDelete] = useState<ClauseListItem | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const loadClauses = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const filters: ClauseFilters = {
        limit: 100, // Load all clauses for category grouping
      }

      if (categoryFilter) filters.category = categoryFilter as ClauseCategory
      if (contractTypeFilter)
        filters.contractType = contractTypeFilter as 'residential' | 'commercial'
      if (statusFilter !== '') filters.isActive = statusFilter === 'true'
      if (search) filters.search = search

      const result = await listClauses(filters)

      if (result.success) {
        setClauses(result.data)
      } else {
        setError('Error al cargar las clausulas')
      }
    } catch {
      setError('Error de conexion')
    } finally {
      setIsLoading(false)
    }
  }, [categoryFilter, contractTypeFilter, statusFilter, search])

  useEffect(() => {
    loadClauses()
  }, [loadClauses])

  // Group clauses by category
  const clausesByCategory = useMemo(() => {
    const grouped: Record<ClauseCategory, ClauseListItem[]> = {
      services: [],
      grace_period: [],
      rent_increase: [],
      renewal: [],
      pets: [],
      modifications: [],
      insurance: [],
      utilities: [],
      other: [],
    }

    clauses.forEach((clause) => {
      if (grouped[clause.category]) {
        grouped[clause.category].push(clause)
      }
    })

    // Sort each category by sortOrder
    Object.keys(grouped).forEach((cat) => {
      grouped[cat as ClauseCategory].sort((a, b) => a.sortOrder - b.sortOrder)
    })

    return grouped
  }, [clauses])

  const toggleCategory = (category: ClauseCategory) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev)
      if (next.has(category)) {
        next.delete(category)
      } else {
        next.add(category)
      }
      return next
    })
  }

  const handleDelete = async () => {
    if (!clauseToDelete) return

    setIsDeleting(true)
    try {
      const result = await deleteClause(clauseToDelete.id)
      if (result.success) {
        setClauses((prev) =>
          prev.map((c) => (c.id === clauseToDelete.id ? { ...c, isActive: false } : c))
        )
        setDeleteModalOpen(false)
        setClauseToDelete(null)
        addToast({
          variant: 'success',
          message: 'La clausula ha sido desactivada exitosamente.',
        })
      }
    } catch (err) {
      console.error('Error deleting clause:', err)
      addToast({
        variant: 'error',
        message: 'No se pudo desactivar la clausula.',
      })
    } finally {
      setIsDeleting(false)
    }
  }

  const handleMoveUp = async (clause: ClauseListItem, categoryItems: ClauseListItem[]) => {
    const index = categoryItems.findIndex((c) => c.id === clause.id)
    if (index <= 0) return

    const newOrder = [...categoryItems]
    const prev = newOrder[index - 1]
    const curr = newOrder[index]
    if (prev && curr) {
      newOrder[index - 1] = curr
      newOrder[index] = prev
    }

    const clauseIds = newOrder.map((c) => c.id)

    try {
      await reorderClauses(clause.category, clauseIds)
      loadClauses()
    } catch (err) {
      console.error('Error reordering:', err)
      addToast({
        variant: 'error',
        message: 'No se pudo reordenar las clausulas.',
      })
    }
  }

  const handleMoveDown = async (clause: ClauseListItem, categoryItems: ClauseListItem[]) => {
    const index = categoryItems.findIndex((c) => c.id === clause.id)
    if (index < 0 || index >= categoryItems.length - 1) return

    const newOrder = [...categoryItems]
    const curr = newOrder[index]
    const next = newOrder[index + 1]
    if (curr && next) {
      newOrder[index] = next
      newOrder[index + 1] = curr
    }

    const clauseIds = newOrder.map((c) => c.id)

    try {
      await reorderClauses(clause.category, clauseIds)
      loadClauses()
    } catch (err) {
      console.error('Error reordering:', err)
      addToast({
        variant: 'error',
        message: 'No se pudo reordenar las clausulas.',
      })
    }
  }

  const openEditModal = (clause: ClauseListItem) => {
    setEditingClause(clause)
    setModalOpen(true)
  }

  const openCreateModal = () => {
    setEditingClause(null)
    setModalOpen(true)
  }

  const openDeleteModal = (clause: ClauseListItem) => {
    setClauseToDelete(clause)
    setDeleteModalOpen(true)
  }

  const handleModalClose = () => {
    setModalOpen(false)
    setEditingClause(null)
  }

  const handleModalSuccess = () => {
    handleModalClose()
    loadClauses()
  }

  return (
    <div>
      <PageHeader
        title="Banco de Clausulas"
        description="Clausulas adicionales reutilizables para incluir en los contratos"
        breadcrumbs={[{ label: 'Inicio', href: '/' }, { label: 'Clausulas' }]}
        actions={
          <Button onClick={openCreateModal}>
            <Plus className="w-4 h-4 mr-1" />
            Nueva Clausula
          </Button>
        }
      />

      {/* Filters */}
      <Card className="mb-6" padding="sm">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
              <Input
                placeholder="Buscar por titulo o descripcion..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>

          <div className="w-44">
            <Select
              options={categoryOptions}
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            />
          </div>

          <div className="w-40">
            <Select
              options={contractTypeOptions}
              value={contractTypeFilter}
              onChange={(e) => setContractTypeFilter(e.target.value)}
            />
          </div>

          <div className="w-36">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
          </div>
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <Card>
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            icon={<BookOpen className="w-12 h-12" />}
            title="Error al cargar"
            description={error}
            action={<Button onClick={loadClauses}>Reintentar</Button>}
          />
        </Card>
      ) : clauses.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BookOpen className="w-12 h-12" />}
            title="Sin clausulas adicionales"
            description={
              search || categoryFilter || contractTypeFilter || statusFilter
                ? 'No se encontraron clausulas con los filtros seleccionados'
                : 'Las clausulas son textos legales reutilizables que puedes agregar a los contratos. Crea clausulas para servicios publicos, incrementos de canon, renovaciones, etc.'
            }
            action={
              search || categoryFilter || contractTypeFilter || statusFilter ? (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearch('')
                    setCategoryFilter('')
                    setContractTypeFilter('')
                    setStatusFilter('')
                  }}
                >
                  Limpiar filtros
                </Button>
              ) : (
                <Button onClick={openCreateModal}>
                  <Plus className="w-4 h-4 mr-1" />
                  Crear Primera Clausula
                </Button>
              )
            }
          />
        </Card>
      ) : (
        <div className="space-y-4">
          {CATEGORY_ORDER.map((category) => {
            const categoryItems = clausesByCategory[category]
            if (categoryItems.length === 0 && categoryFilter && categoryFilter !== category) {
              return null
            }

            const isExpanded = expandedCategories.has(category)

            return (
              <Card key={category} padding="none">
                {/* Category header (accordion toggle) */}
                <button
                  onClick={() => toggleCategory(category)}
                  className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="w-5 h-5 text-neutral-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-neutral-400" />
                    )}
                    <h3 className="font-semibold text-neutral-900">{CATEGORY_LABELS[category]}</h3>
                    <Badge variant="secondary">{categoryItems.length}</Badge>
                  </div>
                </button>

                {/* Category items */}
                {isExpanded && (
                  <div className="border-t border-neutral-200">
                    {categoryItems.length === 0 ? (
                      <div className="px-4 py-6 text-center text-neutral-500 text-sm">
                        No hay clausulas en esta categoria
                      </div>
                    ) : (
                      <div className="divide-y divide-neutral-100">
                        {categoryItems.map((clause, index) => (
                          <div
                            key={clause.id}
                            className="flex items-start gap-4 px-4 py-3 hover:bg-neutral-50 transition-colors"
                          >
                            {/* Reorder handle */}
                            <div className="flex flex-col items-center gap-1 pt-1">
                              <GripVertical className="w-4 h-4 text-neutral-300" />
                              <div className="flex flex-col gap-0.5">
                                <button
                                  onClick={() => handleMoveUp(clause, categoryItems)}
                                  disabled={index === 0}
                                  className="p-0.5 text-neutral-400 hover:text-neutral-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Mover arriba"
                                >
                                  <ArrowUp className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => handleMoveDown(clause, categoryItems)}
                                  disabled={index === categoryItems.length - 1}
                                  className="p-0.5 text-neutral-400 hover:text-neutral-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                  title="Mover abajo"
                                >
                                  <ArrowDown className="w-3 h-3" />
                                </button>
                              </div>
                            </div>

                            {/* Clause content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-medium text-neutral-900 truncate">
                                  {clause.title}
                                </h4>
                                {!clause.isActive && <Badge variant="default">Inactiva</Badge>}
                              </div>
                              <p className="text-sm text-neutral-500 line-clamp-2">
                                {clause.description}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                {clause.contractTypes.map((ct) => (
                                  <Badge
                                    key={ct}
                                    variant={ct === 'residential' ? 'default' : 'secondary'}
                                    className="text-xs"
                                  >
                                    {ct === 'residential' ? 'Residencial' : 'Comercial'}
                                  </Badge>
                                ))}
                              </div>
                            </div>

                            {/* Actions */}
                            <div className="flex items-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditModal(clause)}
                                title="Editar clausula"
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              {clause.isActive && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openDeleteModal(clause)}
                                  className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
                                  title="Desactivar clausula"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Card>
            )
          })}
        </div>
      )}

      {/* Create/Edit Modal */}
      <ClauseModal
        open={modalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
        clause={editingClause}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        title="Desactivar Clausula"
        description="Esta accion desactivara la clausula"
      >
        <p className="text-neutral-600">
          Estas seguro de que deseas desactivar la clausula{' '}
          <span className="font-medium text-neutral-900">{clauseToDelete?.title}</span>? La clausula
          quedara inactiva pero no se eliminara.
        </p>
        <ModalFooter>
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} loading={isDeleting}>
            Desactivar
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
